import { Link } from '@tanstack/react-router'
import { Calendar, Clock, FileWarning, LogOut, UserCheck, UserMinus, Users } from 'lucide-react'

import { useEmployeeStats } from '@/hooks'
import { cn } from '@/lib/utils'

interface StatCardProps {
  title: string
  value: number
  icon: React.ReactNode
  color: string
  bgColor: string
}

function StatCard({ title, value, icon, color, bgColor }: StatCardProps) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', bgColor)}>
          <span className={color}>{icon}</span>
        </div>
        <div>
          <p className="text-2xl font-semibold">{value}</p>
          <p className="text-sm text-muted-foreground">{title}</p>
        </div>
      </div>
    </div>
  )
}

interface EmployeeListSectionProps {
  title: string
  icon: React.ReactNode
  employees: Array<{ id: string; name: string; employeeNo: string }>
  emptyText: string
  showProgress?: boolean
  progressData?: Array<{ employee: { id: string; name: string; employeeNo: string }; onboardingProgress: number }>
}

function EmployeeListSection({ title, icon, employees, emptyText, showProgress, progressData }: EmployeeListSectionProps) {
  const items = showProgress && progressData ? progressData : employees.map((e) => ({ employee: e, onboardingProgress: 100 }))

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        {icon}
        <h3 className="font-medium">{title}</h3>
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs">{items.length}</span>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {items.length === 0 ? (
          <p className="p-4 text-center text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <ul className="divide-y">
            {items.map(({ employee, onboardingProgress }) => (
              <li key={employee.id} className="px-4 py-2 hover:bg-muted/50">
                <Link
                  to="/hr/employees/$employeeId"
                  params={{ employeeId: employee.id }}
                  className="flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium hover:text-primary">{employee.name}</p>
                    <p className="text-xs text-muted-foreground">{employee.employeeNo}</p>
                  </div>
                  {showProgress && onboardingProgress < 100 && (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${onboardingProgress}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">{onboardingProgress}%</span>
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export function EmployeeDashboard() {
  const { data: stats, isLoading, error } = useEmployeeStats()

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg border bg-muted" />
        ))}
      </div>
    )
  }

  if (error || !stats) {
    return null // Silently fail, dashboard is optional
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="在职员工"
          value={stats.byStatus.active}
          icon={<UserCheck className="h-5 w-5" />}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <StatCard
          title="已离职"
          value={stats.byStatus.resigned}
          icon={<UserMinus className="h-5 w-5" />}
          color="text-gray-600"
          bgColor="bg-gray-100"
        />
        <StatCard
          title="休假中"
          value={stats.byStatus.onLeave}
          icon={<Calendar className="h-5 w-5" />}
          color="text-yellow-600"
          bgColor="bg-yellow-100"
        />
        <StatCard
          title="总人数"
          value={stats.total}
          icon={<Users className="h-5 w-5" />}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
      </div>

      {/* Employee Lists */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <EmployeeListSection
          title="最近入职"
          icon={<UserCheck className="h-4 w-4 text-green-600" />}
          employees={[]}
          emptyText="暂无最近入职员工"
          showProgress
          progressData={stats.recentHires}
        />
        <EmployeeListSection
          title="当前休假"
          icon={<Clock className="h-4 w-4 text-yellow-600" />}
          employees={stats.currentlyOnLeave}
          emptyText="暂无休假员工"
        />
        <EmployeeListSection
          title="档案不完整"
          icon={<FileWarning className="h-4 w-4 text-orange-600" />}
          employees={stats.incompleteProfiles}
          emptyText="所有档案已完整"
        />
        <EmployeeListSection
          title="即将离职"
          icon={<LogOut className="h-4 w-4 text-red-600" />}
          employees={stats.upcomingDepartures}
          emptyText="暂无即将离职员工"
        />
        <EmployeeListSection
          title="最近离职"
          icon={<UserMinus className="h-4 w-4 text-gray-600" />}
          employees={stats.recentResignations}
          emptyText="暂无最近离职员工"
        />
      </div>
    </div>
  )
}
